<template>
  <woot-modal :show.sync="show" :on-close="handleCloseWithGuard">
    <div class="column content-box">
        <woot-modal-header
          :header-title="modalTitle"
          :header-content="modalDescription"
        />
        <form class="roww" @submit.prevent="createTicket()">
          <div v-if="shouldShowCompanies" class="company-field">
            <company-picker v-model="companyId" />
          </div>
          <!-- Campo de seleção de contato - exibido apenas quando showContactField é true -->
          <div v-if="showContactField" class="contact-field">
            <label :class="{ error: $v.selectedContact.$error }">
              {{ $t('TICKET.FORM.CONTACT.LABEL') }}{{ shouldShowCompanies ? '' : '*' }}
              <div v-if="!selectedContact" class="contact-search-wrapper">
                <input
                  ref="searchInput"
                  type="text"
                  :placeholder="$t('TICKET.FORM.CONTACT.PLACEHOLDER')"
                  :value="searchQuery"
                  @input="debounceSearch"
                />
                <div v-if="contacts.length > 0 && searchQuery.length > 0" class="contact-search-results">
                  <ul class="search-list">
                    <li v-for="contactItem in contacts" :key="contactItem.id">
                      <search-result-contact-item
                        :id="contactItem.id"
                        :name="contactItem.name"
                        :email="contactItem.email"
                        :phone="contactItem.phone_number"
                        :account-id="accountId"
                        :thumbnail="contactItem.thumbnail"
                        :redirect="false"
                        @click.native="selectContact(contactItem)"
                      />
                    </li>
                  </ul>
                </div>
              </div>
              <div v-else class="selected-contact">
                <div class="selected-contact-info">
                  <woot-thumbnail :src="selectedContact.thumbnail" :username="selectedContact.name" size="24px" />
                  <span class="selected-contact-name">{{ selectedContact.name }}</span>
                </div>
                <woot-button
                  variant="clear"
                  size="tiny"
                  icon="dismiss"
                  color-scheme="secondary"
                  @click="removeSelectedContact"
                />
              </div>
            </label>
            <div class="add-contact-button">
              <woot-button
                variant="smooth"
                size="tiny"
                icon="add"
                @click="openCreateContactModal"
              >
                {{ $t('TICKET.FORM.CONTACT.CREATE_NEW') }}
              </woot-button>
            </div>
          </div>

          <div>
            <label :class="{ error: $v.text.$error }">
              {{ $t('TICKET.FORM.TEXT.LABEL') }}
              <input
                v-model.trim="text"
                type="text"
                :placeholder="$t('TICKET.FORM.TEXT.PLACEHOLDER')"
                @input="$v.text.$touch"
              />
            </label>
          </div>
          <div class="description">
            <label :class="{ error: $v.description.$error }">
              {{ $t('TICKET.FORM.DESCRIPTION.LABEL') }}
              <textarea
                rows="2"
                v-model.trim="description"
                :placeholder="$t('TICKET.FORM.DESCRIPTION.PLACEHOLDER')"
                @input="$v.description.$touch"
              />
            </label>
          </div>

          <div>
            <label :class="{ error: $v.kanbanfunnelSelected.$error }">
              {{ $t('TICKET.FORM.KANBAN.LABEL') }}
              <select v-model="kanbanfunnelSelected">
                <option value="" disabled>{{ this.$t('TICKET.FORM.KANBAN.PLACEHOLDER') }}</option>
                <option v-for="kanban in listKanbanActive" :key="kanban.id" :value="kanban">
                  {{ kanban.name }}
                </option>
              </select>
            </label>
          </div>

          <div v-if="kanbanfunnelSelected">
            <label :class="{ error: $v.columnSelected.$error }">
              {{ $t('TICKET.FORM.COLUMN.LABEL') }}
              <select v-model="columnSelected">
                <option value="" disabled>{{ this.$t('TICKET.FORM.COLUMN.PLACEHOLDER') }}</option>
                <option v-for="colum in kanbanfunnelSelected.columns" :key="colum.id" :value="colum">
                  {{ colum.name }}
                </option>
              </select>
            </label>
          </div>


          <div v-if="isFunnelSale">
            <label>
              {{ $t('TICKET.FORM.SALE_VALUE.LABEL') }}
              <input
                v-money="money"
                v-model="saleValue"
                type="text"
                class="input-margin-bottom"
                :placeholder="$t('TICKET.FORM.SALE_VALUE.PLACEHOLDER')"
              />
            </label>
          </div>

          <div v-if="kanbanfunnelSelected">
            <settings-section
              class="settings-select"
              :title="$t('TICKET.FORM.PRIORITY.LABEL')"
              :sub-title="''"
            >
              <div class="urgency-grid">
                <template v-if="useDynamicPriorityQuestions">
                  <label
                    v-for="(q, qIdx) in dynamicPriorityQuestions"
                    :key="`pq-${qIdx}`"
                  >
                    {{ q.question }}
                    <select v-model.number="priorityAnswers[qIdx]">
                      <option :value="null" disabled>
                        {{ $t('TICKET.FORM.URGENCY.SELECT_PLACEHOLDER') }}
                      </option>
                      <option
                        v-for="(opt, optIdx) in q.options"
                        :key="`pq-${qIdx}-opt-${optIdx}`"
                        :value="opt.weight"
                      >
                        {{ opt.label }}
                      </option>
                    </select>
                  </label>
                </template>
                <template v-else-if="kanbanfunnelSelected">
                  <label>
                    {{ $t('TICKET.FORM.PRIORITY.LABEL') }}
                    <select v-model="manualPriority">
                      <option :value="null" disabled>
                        {{ $t('TICKET.FORM.PRIORITY.PLACEHOLDER') }}
                      </option>
                      <option
                        v-for="opt in manualPriorityOptions"
                        :key="`manual-priority-${opt.value}`"
                        :value="opt.value"
                      >
                        {{ opt.name }}
                      </option>
                    </select>
                  </label>
                </template>
              </div>

              <div class="urgency-summary">
                <span>{{ $t('TICKET.FORM.URGENCY.PRIORITY_LABEL') }}: {{ calculatedPriorityLabel }}</span>
              </div>
            </settings-section>
          </div>

          <div>
            <settings-section
              class="settings-select"
              :title="$t('TICKET.FORM.LABEL.LABEL')"
              :sub-title="$t('TICKET.FORM.LABEL.DESC')"
            >
              <multiselect v-model="labelsIds" :options="labels" track-by="id" label="title" :multiple="true"
                :close-on-select="false" :clear-on-select="false" :hide-selected="true" placeholder="Pick some" selected-label
                :select-label="$t('FORMS.MULTISELECT.CLICK_TO_SELECT')" :deselect-label="$t('FORMS.MULTISELECT.CLICK_TO_REMOVE')"
              />
            </settings-section>

          </div>

          <div class="modal-footer">
            <div class="medium-12 columns">
              <woot-submit-button
                :disabled="isSubmitDisabled || showLoading"
                :button-text="$t('CRM_MGMT.FORM.CREATE')"
                :loading="showLoading"
                class="bg-primary"
              />
              <button class="button clear" @click.prevent="handleCloseWithGuard">
                {{ $t('CRM_MGMT.FORM.CANCEL') }}
              </button>
            </div>
          </div>
        </form>
      </div>


      <!-- Modal para criar novo contato -->
      <woot-modal
        :show.sync="showCreateContactModal"
        :on-close="handleCreateContactClose"
        modal-type="right-aligned"
      >
        <div class="column content-box">
          <woot-modal-header
            :header-title="$t('CREATE_CONTACT.TITLE')"
            :header-content="$t('CREATE_CONTACT.DESC')"
          />
          <contact-form
            ref="createContactForm"
            :in-progress="uiFlags.isCreating"
            :on-submit="onContactSubmit"
            @success="onContactSuccess"
            @cancel="handleCreateContactClose"
          />
        </div>
      </woot-modal>

      <!-- Modal para confirmação de sobreescrita de empresa -->
      <woot-modal
        :show.sync="showCompanyOverrideModal"
        :on-close="closeCompanyOverrideModal"
        modal-type="alert"
      >
        <div class="column content-box company-override-modal">
          <woot-modal-header
            :header-title="$t('TICKET.FORM.COMPANY_OVERRIDE.TITLE')"
            :header-content="$t('TICKET.FORM.COMPANY_OVERRIDE.DESC')"
          />
          <div class="modal-footer with-spacing">
            <div class="medium-12 columns">
              <woot-submit-button
                :button-text="$t('TICKET.FORM.COMPANY_OVERRIDE.CONFIRM')"
                :loading="showLoading"
                class="bg-primary"
                @click="confirmCompanyOverride"
              />
              <button class="button clear" @click.prevent="closeCompanyOverrideModal">
                {{ $t('TICKET.FORM.COMPANY_OVERRIDE.CANCEL') }}
              </button>
            </div>
          </div>
        </div>
      </woot-modal>

      <!-- Modal de confirmação para dados não salvos -->
      <woot-delete-modal
        :show.sync="showUnsavedChangesConfirm"
        :on-close="cancelUnsavedClose"
        :on-confirm="confirmUnsavedClose"
        :title="$t('CONFIRM_UNSAVED_CHANGES.TITLE')"
        :message="$t('CONFIRM_UNSAVED_CHANGES.MESSAGE')"
        :confirm-text="$t('CONFIRM_UNSAVED_CHANGES.CONFIRM')"
        :reject-text="$t('CONFIRM_UNSAVED_CHANGES.CANCEL')"
      />
  </woot-modal>
</template>
<script>
import { mapGetters } from 'vuex';
import { required, minLength, requiredIf } from 'vuelidate/lib/validators';
import SettingsSection from '../../../../components/SettingsSection.vue';
import SearchResultContactItem from '../../../../modules/search/components/SearchResultContactItem.vue';
import CompanyPicker from '../../../../components/widgets/CompanyPicker.vue';
import ContactForm from './ContactForm.vue';
import unsavedChangesGuard from 'shared/mixins/unsavedChangesGuard';

const requiredSelectValue = value =>
  value !== null &&
  value !== undefined &&
  value !== '' &&
  value !== 'null' &&
  !(typeof value === 'number' && Number.isNaN(value));

export default {
  name: 'ModalNewTicket',
  components: {
    SettingsSection,
    SearchResultContactItem,
    CompanyPicker,
    ContactForm,
  },
  mixins: [unsavedChangesGuard],
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    contact: {
      type: Object,
      default: () => ({}),
    },
    showContactField: {
      type: Boolean,
      default: false,
    },
    preselectedKanban: {
      type: Object,
      default: null,
    }
  },
  data(){
    return {
      text: '',
      description: '',
      companyId: null,
      kanbanfunnelSelected: null,
      columnSelected: null,
      labelsIds: [],
      labelSelected: null,
      showLoading: false,
      saleValue: null,
      searchQuery: '',
      selectedContact: null,
      debounce: null,
      priorityAnswers: [],
      manualPriority: null,
      showCreateContactModal: false,
      showCompanyOverrideModal: false,
      companyOverrideConfirmed: false,
      showUnsavedChangesConfirm: false,
      money: {
        decimal: ',',
        thousands: '.',
        prefix: 'R$ ',
        suffix: '',
        precision: 2
      }
    }
  },
  validations: {
    text: {
      required,
      minLength: minLength(2),
    },
    description: {},
    kanbanfunnelSelected: {
      required,
    },
    columnSelected: {
      required,
    },
    selectedContact: {
      required: requiredIf(function() {
        return this.showContactField && !this.shouldShowCompanies;
      })
    }
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      labels: 'labelsCrm/getLabels',
      currentUser: 'getCurrentUser',
      kanbanList: 'getKanbanfunnels',
      accountId: 'getCurrentAccountId',
      contactRecords: 'conversationSearch/getContactRecords',
      uiFlags: 'contacts/getUIFlags',
    }),

    currentAccount() {
      return this.getAccount(this.accountId) || {};
    },

    shouldShowCompanies() {
      const hasCompaniesEnabled = !!this.currentAccount?.activate_companies;
      const kanban = this.kanbanfunnelSelected || this.preselectedKanban;
      const isTicketsType = kanban?.type_opportunity === 'tickets';


      return hasCompaniesEnabled && !isTicketsType;
    },

    contacts() {
      return this.contactRecords
        ?.map(contact => ({
          ...contact,
          type: 'contact',
        }))
        .filter(contact => !contact?.phone_number?.includes('@g.us')) || [];
    },

    useDynamicPriorityQuestions() {
      return (
        !!this.kanbanfunnelSelected?.enable_priority_questions &&
        Array.isArray(this.kanbanfunnelSelected?.priority_questions) &&
        this.kanbanfunnelSelected.priority_questions.length > 0
      );
    },

    dynamicPriorityQuestions() {
      if (!this.useDynamicPriorityQuestions) return [];
      return (this.kanbanfunnelSelected.priority_questions || [])
        .map(q => ({
          question: q?.question || '',
          options: (q?.options || [])
            .map(opt => ({
              label: opt?.label || '',
              weight: Number.isFinite(Number(opt?.weight)) ? Number(opt.weight) : 0,
            }))
            .filter(opt => opt.label),
        }))
        .filter(q => q.question && q.options.length > 0);
    },

    manualPriorityOptions() {
      return [
        { name: this.$t('TICKET.FORM.PRIORITY.OPTIONS.CRITICAL'), value: 'critical' },
        { name: this.$t('TICKET.FORM.PRIORITY.OPTIONS.HIGH'), value: 'high' },
        { name: this.$t('TICKET.FORM.PRIORITY.OPTIONS.MEDIUM'), value: 'medium' },
        { name: this.$t('TICKET.FORM.PRIORITY.OPTIONS.LOW'), value: 'low' },
      ];
    },

    isFunnelSale(){
      return this.kanbanfunnelSelected?.enable_sales_funnel;
    },

    isSubmitDisabled() {
      const hasValidText = (this.text || '').trim().length >= 2;
      const hasKanban = !!this.kanbanfunnelSelected?.id;
      const hasColumn = !!this.columnSelected?.id;
      const hasUrgency = this.isUrgencyComplete;
      const hasContact = this.showContactField
        ? (this.shouldShowCompanies ? true : !!this.selectedContact?.id)
        : true;

      return !(hasValidText && hasKanban && hasColumn && hasUrgency && hasContact);
    },

    urgencyScore() {
      if (this.useDynamicPriorityQuestions) {
        return this.dynamicPriorityQuestions.reduce((acc, _q, idx) => {
          const n = Number(this.priorityAnswers?.[idx]);
          return acc + (Number.isFinite(n) ? n : 0);
        }, 0);
      }
      return 0;
    },

    isUrgencyComplete() {
      const isFilled = v => requiredSelectValue(v);

      if (this.useDynamicPriorityQuestions) {
        return this.dynamicPriorityQuestions.every((_q, idx) =>
          isFilled(this.priorityAnswers?.[idx])
        );
      }

      return isFilled(this.manualPriority);
    },

    priorityFromUrgencyScore() {
      if (this.urgencyScore >= 100) return 'critical';
      if (this.urgencyScore >= 70) return 'high';
      if (this.urgencyScore >= 40) return 'medium';
      return 'low';
    },

    calculatedPriority() {
      if (!this.useDynamicPriorityQuestions) return null;
      const anyAnswered = this.dynamicPriorityQuestions.some((_q, idx) =>
        requiredSelectValue(this.priorityAnswers?.[idx])
      );
      if (!anyAnswered) return null;
      return this.priorityFromUrgencyScore;
    },

    calculatedPriorityLabel() {
      const p = this.useDynamicPriorityQuestions
        ? this.calculatedPriority
        : this.manualPriority;
      if (!p) return '-';
      if (p === 'critical') return this.$t('TICKET.FORM.PRIORITY.OPTIONS.CRITICAL');
      if (p === 'high') return this.$t('TICKET.FORM.PRIORITY.OPTIONS.HIGH');
      if (p === 'medium') return this.$t('TICKET.FORM.PRIORITY.OPTIONS.MEDIUM');
      return this.$t('TICKET.FORM.PRIORITY.OPTIONS.LOW');
    },

    listKanbanActive(){
      return this.kanbanList?.filter((kanban) => kanban.active);
    },

    modalTitle() {
      const kanban = this.kanbanfunnelSelected || this.preselectedKanban;
      if (!kanban || !kanban.type_opportunity) {
        return this.$t('TICKET.CREATE.TITLE');
      }
      const isBusinessOpportunity = kanban.type_opportunity === 'business' || kanban.type_opportunity === 'both';
      return isBusinessOpportunity ? this.$t('TICKET.CREATE.BUSINESS_TITLE') : this.$t('TICKET.CREATE.TITLE');
    },

    modalDescription() {
      const kanban = this.kanbanfunnelSelected || this.preselectedKanban;
      if (!kanban || !kanban.type_opportunity) {
        return this.$t('TICKET.CREATE.DESC');
      }
      const isBusinessOpportunity = kanban.type_opportunity === 'business' || kanban.type_opportunity === 'both';
      return isBusinessOpportunity ? this.$t('TICKET.CREATE.BUSINESS_DESC') : this.$t('TICKET.CREATE.DESC');
    },

    isBusinessOpportunityModal() {
      const kanban = this.kanbanfunnelSelected || this.preselectedKanban;
      return kanban && (kanban.type_opportunity === 'business' || kanban.type_opportunity === 'both');
    },

    contactToUse() {
      return this.showContactField ? this.selectedContact : this.contact;
    },

    needsCompanyOverride() {
      return false;
    }
  },
  methods: {
    resetPriorityAnswers() {
      if (!this.useDynamicPriorityQuestions) {
        this.priorityAnswers = [];
        return;
      }

      const size = this.dynamicPriorityQuestions.length;
      this.priorityAnswers = Array.from({ length: size }, () => null);
    },

    onClose() {
      this.onCancel();
    },

    onCancel() {
      this.clearSearch();
      // Limpar seleção de kanban para não persistir entre aberturas
      this.kanbanfunnelSelected = null;
      this.columnSelected = null;
      this.text = '';
      this.description = '';
      this.labelsIds = [];
      this.saleValue = null;
      this.priorityAnswers = [];
      this.manualPriority = null;
      this.companyOverrideConfirmed = false;
      this.showCompanyOverrideModal = false;
      this.$emit('cancel');
    },
    onSuccess() {
      this.$emit('success');
    },

    async createTicket(){
      if(!this.isFunnelSale){
        this.saleValue = null;
      }

      // Buscar contato completo para verificar company_id
      let contactWithCompany = this.contactToUse;
      if (this.shouldShowCompanies && this.companyId && this.contactToUse) {
        try {
          await this.$store.dispatch('contacts/show', { id: this.contactToUse.id });
          contactWithCompany = this.$store.getters['contacts/getContact'](this.contactToUse.id);
        } catch (error) {
          console.error('Error fetching contact details:', error);
        }
      }

      // Verificar se precisa de sobreescrita de empresa
      const contactCompanyId = contactWithCompany?.company_id;
      const needsOverride = contactCompanyId && contactCompanyId !== this.companyId;

      if (needsOverride && !this.companyOverrideConfirmed) {
        this.showCompanyOverrideModal = true;
        return;
      }

      const numericSaleValue = this.saleValue
        ? Number(this.saleValue.replace(/[^\d]/g, '')) / 100
        : null;

      let finalCompanyId = this.shouldShowCompanies ? this.companyId : null;

      try {
        if (this.shouldShowCompanies && this.companyId && contactWithCompany && !contactWithCompany.company_id) {
          await this.$store.dispatch('contacts/update', {
            id: contactWithCompany.id,
            company_id: this.companyId
          });
          if (this.showContactField && this.selectedContact) {
            this.selectedContact.company_id = this.companyId;
          } else if (this.contact && this.contact.id === contactWithCompany.id) {
            this.contact.company_id = this.companyId;
          }
          finalCompanyId = this.companyId;
        }

        if (this.shouldShowCompanies && this.companyId && contactWithCompany &&
            contactWithCompany.company_id && contactWithCompany.company_id !== this.companyId &&
            this.companyOverrideConfirmed) {
          await this.$store.dispatch('contacts/update', {
            id: contactWithCompany.id,
            company_id: this.companyId
          });
          if (this.showContactField && this.selectedContact) {
            this.selectedContact.company_id = this.companyId;
          } else if (this.contact && this.contact.id === contactWithCompany.id) {
            this.contact.company_id = this.companyId;
          }
          finalCompanyId = this.companyId;
        }
      } catch (error) {
        console.error('Error updating contact company:', error);
        this.showAlert(this.$t('TICKET.CREATE.API.ERROR_MESSAGE'));
        return;
      }

      const payload = {
        user_id: this.currentUser.id,
        contact_id: this.contactToUse?.id,
        company_id: finalCompanyId,
        priority: this.useDynamicPriorityQuestions
          ? this.calculatedPriority
          : this.manualPriority,
        urgency_answers: this.useDynamicPriorityQuestions ? this.priorityAnswers : [],
        text: this.text,
        description: this.description,
        kanbanfunnel_id: this.kanbanfunnelSelected?.id,
        columns_id: this.columnSelected?.id,
        labels_ids: this.labelsIds?.map((label) => label.id),
        sale_value: numericSaleValue
      }

      await this.$store.dispatch('createTicket', payload);
      const message = this.isBusinessOpportunityModal
        ? this.$t('TICKET.CREATE.API.BUSINESS_SUCCESS_MESSAGE')
        : this.$t('TICKET.CREATE.API.SUCCESS_MESSAGE');
      this.showAlert(message);
      this.$emit('success');
      this.onCancel();
    },

    showAlert(message) {
      bus.$emit('newToastMessage', message);
    },

    debounceSearch(e) {
      this.searchQuery = e.target.value;

      clearTimeout(this.debounce);
      this.debounce = setTimeout(async () => {
        if (this.searchQuery.length > 2 || this.searchQuery.match(/^[0-9]+$/)) {
          this.onSearch(this.searchQuery);
        }
      }, 500);
    },

    onSearch(q) {
      if (!q) {
        this.$store.dispatch('conversationSearch/clearSearchResults');
        return;
      }
      this.$store.dispatch('conversationSearch/fullSearch', { q });
    },

    selectContact(contact) {
      this.selectedContact = contact;
      this.searchQuery = '';
      this.$store.dispatch('conversationSearch/clearSearchResults');
    },

    removeSelectedContact() {
      this.selectedContact = null;
      this.searchQuery = '';
    },

    clearSearch() {
      this.searchQuery = '';
      this.selectedContact = null;
      this.$store.dispatch('conversationSearch/clearSearchResults');
    },

    openCreateContactModal() {
      this.showCreateContactModal = true;
    },

    closeCreateContactModal() {
      this.showCreateContactModal = false;
    },

    /**
     * Intercepta o fechamento do modal de criar contato para verificar alterações.
     */
    handleCreateContactClose() {
      // Verifica se o contact-form tem alterações não salvas
      if (this.$refs.createContactForm && this.$refs.createContactForm.hasUnsavedChanges) {
        const hasChanges = this.$refs.createContactForm.hasUnsavedChanges(
          this.$refs.createContactForm.getFormState()
        );
        if (hasChanges) {
          // Delega para o contact-form mostrar a confirmação
          this.$refs.createContactForm.handleCancelWithGuard();
          return;
        }
      }
      // Sem alterações, fecha normalmente
      this.closeCreateContactModal();
    },

    async onContactSubmit(contactItem) {
      try {
        const response = await this.$store.dispatch('contacts/create', contactItem);
        console.log('Debug - response do create:', response);
        // Chamar onContactSuccess diretamente com os dados do contato
        await this.onContactSuccess(response);
        return response;
      } catch (error) {
        console.error('Error creating contact:', error);
        throw error;
      }
    },

      onContactSuccess(contactData) {
      // Após criar o contato com sucesso, selecioná-lo no formulário
      if (contactData && contactData.id) {
        this.selectedContact = contactData;
        this.searchQuery = '';
        this.$store.dispatch('conversationSearch/clearSearchResults');
      }
      this.closeCreateContactModal();
      this.showAlert(this.$t('TICKET.FORM.CONTACT.CREATE_SUCCESS'));
    },

    closeCompanyOverrideModal() {
      this.showCompanyOverrideModal = false;
      this.companyOverrideConfirmed = false;
    },

    confirmCompanyOverride() {
      this.companyOverrideConfirmed = true;
      this.showCompanyOverrideModal = false;
      // Continuar com a criação do ticket
      this.createTicket();
    },

    /**
     * Retorna o estado atual do formulário para comparação.
     * Deve incluir todos os campos editáveis.
     */
    getFormState() {
      return {
        text: this.text,
        description: this.description,
        companyId: this.companyId,
        kanbanfunnelSelected: this.kanbanfunnelSelected?.id || null,
        columnSelected: this.columnSelected?.id || null,
        labelsIds: this.labelsIds?.map((label) => label.id).sort(),
        saleValue: this.saleValue,
        selectedContact: this.selectedContact?.id || null,
        priorityAnswers: [...this.priorityAnswers],
        manualPriority: this.manualPriority,
      };
    },

    /**
     * Intercepta o fechamento do modal para verificar alterações não salvas.
     */
    handleCloseWithGuard() {
      this.handleUnsavedClose(
        this.getFormState(),
        () => this.onCancel(),
        () => { this.showUnsavedChangesConfirm = true; }
      );
    },
  },

  watch: {
    show: {
      immediate: true,
      handler(newVal, oldVal) {
        console.log('[ModalNewTicket] Watch show executando - newVal:', newVal, 'oldVal:', oldVal);
        if (newVal) {
          console.log('[ModalNewTicket] Modal abrindo/visível, resetando e capturando estado inicial...');
          this.resetPriorityAnswers();
          // Se tem kanban pré-selecionado, usar ele
          if (this.preselectedKanban && !this.kanbanfunnelSelected) {
            this.kanbanfunnelSelected = this.preselectedKanban;
          }
          // Captura estado inicial após abrir o modal para detectar alterações
          // Usa setTimeout para garantir que todos os dados estejam prontos
          setTimeout(() => {
            console.log('[ModalNewTicket] setTimeout executando, chamando captureInitialState...');
            this.captureInitialState(this.getFormState());
            console.log('[ModalNewTicket] Estado inicial capturado:', this.getFormState());
            console.log('[ModalNewTicket] initialFormState agora é:', this.initialFormState);
          }, 100);
        }
      }
    },

    kanbanfunnelSelected(newVal) {
      if (newVal) {
        this.columnSelected = null;
        this.resetPriorityAnswers();
        this.manualPriority = null;
      }
    }
  }

};
</script>

<style scoped>
.form {
  padding: var(--space-normal) var(--space-large) var(--space-large);
}

.card-content {
  display: flex;
  gap: 2rem;
}

.input-margin-bottom {
  margin-bottom: 16px;
}

.description{
  min-width: 220px;
}

.contact-field {
  margin-bottom: var(--space-normal);
}

.contact-search-wrapper {
  position: relative;
}

.contact-search-results {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-normal);
  max-height: 200px;
  overflow-y: auto;
  z-index: 100;
  box-shadow: var(--shadow-large);
}

.search-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.search-list li {
  border-bottom: 1px solid var(--color-border-light);
}

.search-list li:last-child {
  border-bottom: none;
}

.selected-contact {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-small);
  background: var(--s-50);
  border-radius: var(--border-radius-normal);
  border: 1px solid var(--color-border);
}

.selected-contact-info {
  display: flex;
  align-items: center;
  gap: var(--space-small);
}

.selected-contact-name {
  font-weight: var(--font-weight-medium);
}

.add-contact-button {
  margin-top: var(--space-small);
}

.company-override-modal .modal-footer.with-spacing {
  margin-top: var(--space-xl);
  padding-top: var(--space-large);
}

.company-override-modal .modal-footer.with-spacing .button:not(:last-child) {
  margin-right: var(--space-small);
}

</style>
